// The map's root element is constantly getting modifier HTML classes set on it based on its current zoom and congestion (as described below).
// These classes allow the enclosed markers to be displayed according to CSS rules.
// See `app/stylesheets/online/apps/shared/_experience_marker.scss`.
//
// Map Zoom Modifiers
// Whenever the map zoom changes we set an HTML class modifier on the map's element eg. `zoom_3x` (actually `online_app__main__map--zoom_3x`).
// You can see the mapping between the Google map's zoom level and our labeling in zoom_label_calculator.js.
// These classes allow the enclosed markers to be displayed according to CSS rules.
//
// Map Marker Congestion Modifiers
// Whenever the map markers are refreshed (changing filters for example) we look at the number of markers and
// set an HTML class modifier on the map's element eg. `congestion_max (actually `online_app__main__map--congestion_max`) based on the congestion level.
// The congestion level is based on the number of markers for the entire map (note that this is *all* the markers, not just the ones that are clustered).
// You can see the threshold levels in marker_congestion_calculator.js.
//
// Clustering
// There is one clusterer used for the map.
// Much of the work around the clusterer can be seen in the `OnlineAppMap.replaceAllExperienceMarkers` method.
// This method is called when filters are changed.
// Importantly, the clusterer *only* clusters experience markers which are not monetizable with benefits ie. basically, experiences that are not on a paid plan.
// The experience markers that *are* monetizable with benefits (basically experiences on a paid plan) are never clustered and are added directly to the map.
// This means that experiences on a paid plan always appear as markers (not clustered) while other experiences "disappear" into clusters.
// The `ClustererCongestionCalculator` looks at the number of markers that are in the clusterer.
// It is then used to dynamically adjust the clusterer's maxZoom and minimumClusterSize based on congestion within the clusterer.
// Note that this is separate from the previously mentioned marker congestion that is for all the map's markers.
//
// Clusterer maxZoom
// The clusterer's maxZoom is dymamically adjusted based on the marker congestion level (see clusterer_congestion_calculator.js).
// The higher the congestion, the higher the maxZoom - meaning that with more markers the clustering continues as you zoom in more.
// And with less markers, the clustering will stop earlier as you zoom in.
//
// Clusterer minimumClusterSize
// The clusterer's minimumClusterSize is dymamically adjusted based on the marker congestion level (see clusterer_congestion_calculator.js).
// The higher the congestion, the higher the minimumClusterSize - meaning that with more markers the clustering happens with bigger groups of markers.
// And with less markers, the clustering will happen more readily with smaller groups of markers.
//

import {MarkerClusterer, SuperClusterViewportAlgorithm} from "@googlemaps/markerclusterer";
import {Dropdown} from "../../components/dropdown/dropdown";
import OnlineAppComponent from "../lib/online_app_component";
import ClustererCongestionCalculator from "../helpers/clusterer_congestion_calculator";
import MarkerCongestionCalculator from "../helpers/marker_congestion_calculator";
import getZoomLabel from "../helpers/zoom_label_calculator";

export const OnlineAppMapConfig = {
    baseHtmlClass: 'online_app__main__map',
    selector: '.online_app__main__map'
}

export class OnlineAppMap extends OnlineAppComponent {

    static getGoogleMarkers(experienceMarkers) {
        return experienceMarkers.map(function (experienceMarker) {
            experienceMarker.googleMarker.experienceMarker = experienceMarker;
            return experienceMarker.googleMarker;
        });
    }

    constructor(id_or_element, onlineApp) {
        super(id_or_element, onlineApp, OnlineAppMapConfig);
        if(!this.element) { return };

        this.initialZoom = 6;
        this.experienceMarkers = [];
        this.currentAdHocExperienceId = null; // id of a focused out-of-filter experience rendered as an ad-hoc pin (reveal resolver step 4)

        this.googleMapsApi = google.maps; // Always access the Google Maps API via this reference! Can be replaced with our own wrapper in the future.
        this.googleMap = new google.maps.Map(this.element, this.getGoogleMapOptions());

        this.setCurrentZoom();
        this.setCurrentMarkerCongestion();

        // The marker clusterer is (re)built per filter change so its algorithm can carry the
        // congestion-adjusted maxZoom / minimumClusterSize — the modern @googlemaps/markerclusterer
        // sets these on the algorithm at construction and has no runtime setters. See buildClusterer.
        this.clusterRenderer = this.getClusterRenderer();
        this.markerClusterer = null;

        this.googleMapsApi.event.addListener(this.googleMap, 'click', function () {
            this.dispatchAction('mapClicked');
        }.bind(this));

        this.googleMapsApi.event.addListener(this.googleMap, 'drag', function () {
            this.dispatchAction('mapDragged');
        }.bind(this));

        this.googleMapsApi.event.addListener(this.googleMap, 'zoom_changed', function () {
            this.setCurrentZoom();
        }.bind(this));

        // Custom Rankers-themed controls replacing Google's disabled zoom + map-type controls
        // (see getGoogleMapOptions). Added after the map exists since they read this.googleMap.
        this.addZoomControl();
        this.addMapTypeControl();

        // When idle we set the marker congestion based on the markers in the map's current bounds.
        // (as opposed to being based on all the map's markers).
        // This is heavy lifting and can result in performance issues - hence this is currently disabled.
        // this.idleTicking = false;
        // this.googleMapsApi.event.addListener(this.googleMap, 'idle', () => {
        //     if (!this.idleTicking) {
        //         requestAnimationFrame((timestamp) => {
        //             this.idleTicking = false;
        //             this.removeHtmlClassState(`congestion_${ this.currentMarkerCongestion }`);
        //             this.currentMarkerCongestion = new MarkerCongestionCalculator(this.getMarkersInBounds().length).congestionLevel;
        //             this.addHtmlClassState(`congestion_${ this.currentMarkerCongestion }`);
        //         });
        //     };
        //     this.idleTicking = true;
        // });

    }

    // getMarkersInBounds() {
    //     const bounds = this.googleMap.getBounds();
    //     return OnlineAppMap.getGoogleMarkers(this.experienceMarkers).filter( (googleMarker) => {
    //         return bounds.contains(googleMarker.getPosition());
    //     });
    // }

    setCurrentMarkerCongestion() {
        this.removeHtmlClassState(`congestion_${ this.currentMarkerCongestion }`);
        this.currentMarkerCongestion = new MarkerCongestionCalculator(this.experienceMarkers.length).congestionLevel;
        this.addHtmlClassState(`congestion_${ this.currentMarkerCongestion }`);
    }

    getGoogleMapOptions() {
        return {
            mapId: this.onlineApp.googleMapsMapId, // Required for AdvancedMarkerElement (Phase 2b); enables vector/cloud map config.
            center: {lat: -41, lng: 172},
            zoom: this.initialZoom,
            minZoom: 5,
            maxZoom: 20, // an explicit upper bound so the custom zoom-in button can disable at max (street/building level; ample for a camping map)
            streetViewControl: false,
            fullscreenControl: false,
            mapTypeId: 'terrain',
            // Google's stock zoom + map-type controls are disabled: their button chrome can't be
            // CSS-themed, so we replace them with custom Rankers-styled controls (addZoomControl /
            // addMapTypeControl). Positions are preserved (zoom bottom-right, map-type top-right).
            // cameraControl (the vector-map pan/rotate/tilt widget) is disabled too — it's on by
            // default for vector maps, un-themeable, and the app has no use for rotate/tilt.
            zoomControl: false,
            mapTypeControl: false,
            cameraControl: false,
            scrollwheel: false
        };
    }

    // Custom zoom control — a stacked +/− pair reusing the app's floating-map-button styling
    // (see _online_app__map_zoom_control.scss, which mirrors the nav-toggler button). Pushed to
    // RIGHT_BOTTOM to preserve Google's default zoom position.
    addZoomControl() {
        const wrap = document.createElement('div');
        wrap.classList.add('online_app__map_zoom_control');

        this.zoomInButton = this.buildZoomButton('online_app__map_zoom_control__button--in', '+', 'Zoom in', 1);
        this.zoomOutButton = this.buildZoomButton('online_app__map_zoom_control__button--out', '−', 'Zoom out', -1);
        wrap.appendChild(this.zoomInButton);
        wrap.appendChild(this.zoomOutButton);

        this.googleMap.controls[this.googleMapsApi.ControlPosition.RIGHT_BOTTOM].push(wrap);

        // Keep each button disabled (native `disabled` → also styled via :disabled) at the map's
        // zoom bounds so a user can't click past min/maxZoom.
        this.googleMapsApi.event.addListener(this.googleMap, 'zoom_changed', this.updateZoomButtonsDisabledState.bind(this));
        this.updateZoomButtonsDisabledState();
    }

    updateZoomButtonsDisabledState() {
        const zoom = this.googleMap.getZoom();
        const minZoom = this.googleMap.get('minZoom');
        const maxZoom = this.googleMap.get('maxZoom');
        this.zoomOutButton.disabled = (minZoom != null && zoom <= minZoom);
        this.zoomInButton.disabled = (maxZoom != null && zoom >= maxZoom);
    }

    buildZoomButton(modifierClass, glyph, ariaLabel, delta) {
        const button = document.createElement('button');
        button.type = 'button';
        button.classList.add('online_app__map_zoom_control__button', modifierClass);
        button.setAttribute('aria-label', ariaLabel);
        button.textContent = glyph;
        button.addEventListener('click', () => {
            this.googleMap.setZoom(this.googleMap.getZoom() + delta);
        });
        return button;
    }

    // Custom map-type control — the shared Dropdown component (select mode) replacing Google's
    // un-themeable map-type dropdown. Reuses the app's dropdown styling; pushed to TOP_RIGHT to
    // preserve Google's previous map-type position. The Dropdown auto-inits once its markup enters
    // the DOM (the component observer watches documentElement/subtree, which covers Google's
    // control container).
    addMapTypeControl() {
        const {trigger, dropdown} = Dropdown.build({
            trigger: {label: 'Terrain', className: 'online_app__map_type_control__trigger'},
            showChevron: true,
            selected: 'terrain', // preselects the trigger label; the map already defaults to terrain
            updateTriggerLabelOnSelect: true,
            items: [ // all select items (each carries a `value`)
                {label: 'Map', value: 'roadmap'},
                {label: 'Terrain', value: 'terrain'},
                {label: 'Satellite', value: 'satellite'},
                {label: 'Hybrid', value: 'hybrid'},
            ],
        });

        const wrap = document.createElement('div');
        wrap.classList.add('online_app__map_type_control');
        wrap.appendChild(trigger);
        wrap.appendChild(dropdown);

        // Event name from Config, not a literal. fireElementEvent stores the memo in event.detail.
        dropdown.addEventListener(Dropdown.config.selectedEventName, (event) => {
            this.googleMap.setMapTypeId(event.detail.value);
        });

        this.googleMap.controls[this.googleMapsApi.ControlPosition.TOP_RIGHT].push(wrap);
    }

    // Build (or rebuild) the marker clusterer for the given google markers, with the algorithm's
    // maxZoom / minimumClusterSize taken from the congestion calculator. The modern
    // @googlemaps/markerclusterer has no runtime setters for these, so a filter change that alters
    // congestion recreates the clusterer (this mirrors the old setMaxZoom/setMinimumClusterSize calls).
    buildClusterer(googleMarkers, clustererCongestionCalculator) {
        this.destroyClusterer();
        // SuperClusterViewportAlgorithm re-queries supercluster.getClusters() whenever the viewport
        // state changes, but only load()s points when markers are non-empty — so an empty clusterer
        // throws ("reading 'range'") on its first render (e.g. on boot with no map filter, or a filter
        // whose experiences are all paid/direct markers). Skip building a clusterer with nothing to cluster.
        if (!googleMarkers.length) return;
        this.markerClusterer = new MarkerClusterer({
            map: this.googleMap,
            markers: googleMarkers,
            renderer: this.clusterRenderer,
            algorithm: new SuperClusterViewportAlgorithm({
                maxZoom: clustererCongestionCalculator.maxZoom,
                minPoints: clustererCongestionCalculator.minimumClusterSize, // supercluster minPoints == old minimumClusterSize
                viewportPadding: 60, // only cluster markers in (a padded) view — far cheaper per idle for large datasets
            }),
            onClusterClick: (event, cluster, map) => this.handleClusterClick(event, cluster, map),
        });
    }

    // A cluster is drawn as a lightweight google.maps.Marker: the app's cluster-icon PNG scaled by
    // count magnitude (53->90px) with the count as a centred white label. Ports the old
    // markerclustererplus `styles` (cluster_icon.png + white count) and matches the library's own
    // DefaultRenderer. Deliberately NOT an AdvancedMarkerElement: the clusterer recreates every cluster
    // marker on each map idle (zoom/pan), and a plain Marker is far cheaper to recreate than an
    // AdvancedMarkerElement with HTML content — this keeps zoom/pan responsive on large datasets.
    getClusterRenderer() {
        const clusterIconPath = `/images/online/apps/${this.onlineApp.code.toLowerCase()}/map/cluster_icon.png`;
        return {
            render: ({ count, position }) => {
                const size = clusterIconSizeForCount(count);
                return new google.maps.Marker({
                    position,
                    icon: {
                        url: clusterIconPath,
                        scaledSize: new google.maps.Size(size, size),
                        labelOrigin: new google.maps.Point(size / 2, size / 2),
                    },
                    label: {
                        text: String(count),
                        color: '#ffffff',
                        fontSize: '13px',
                        fontWeight: 'bold',
                    },
                    zIndex: 1000 + count,
                });
            }
        };
    }

    // Modified cluster-click zoom: expand the cluster's bounds by 10% of its width on all sides so the
    // zoom-in is gentler than a tight fit (ports the old `clusterclick` handler; the old `zoomOnClick`
    // is off because onClusterClick owns the behaviour).
    handleClusterClick(event, cluster, map) {
        this.dispatchAction('clusterSelected', cluster);
        const clusterBounds = cluster.bounds;
        if (!clusterBounds) return;

        const ne = clusterBounds.getNorthEast();
        const sw = clusterBounds.getSouthWest();
        const offset = (ne.lng() - sw.lng()) * 0.1; // 10% of the width

        clusterBounds.extend(new this.googleMapsApi.LatLng({ lat: ne.lat() + offset, lng: ne.lng() + offset }));
        clusterBounds.extend(new this.googleMapsApi.LatLng({ lat: sw.lat() - offset, lng: sw.lng() - offset }));

        this.googleMap.fitBounds(clusterBounds);
    }

    goToCoords(lat, lng, zoom) {
        const center = new this.googleMapsApi.LatLng(lat, lng);
        this.googleMap.panTo(center);
        if (zoom) {
            this.googleMap.setZoom(parseInt(zoom))
        }
    }

    panBy(xOffset, yOffset) {
        this.googleMap.panBy(xOffset, yOffset);
    }

    replaceAllExperienceMarkers(newExperienceMarkers) {
        this.clearMarkersFromMap();
        this.experienceMarkers = newExperienceMarkers;

        let experienceMarkersWithBenefits = [];
        let experienceMarkersWithOutBenefits = [];

        this.experienceMarkers.forEach( (experienceMarker) => {
            if (experienceMarker.monetizableWithBenefits()) {
                experienceMarkersWithBenefits.push(experienceMarker);
            } else {
                experienceMarkersWithOutBenefits.push(experienceMarker);
            }
        });

        this.setCurrentMarkerCongestion();

        // (Re)build the clusterer with congestion-adjusted maxZoom/minimumClusterSize and the
        // non-benefit markers. Benefit (paid) markers are added directly to the map and never cluster.
        const calc = new ClustererCongestionCalculator(experienceMarkersWithOutBenefits.length);
        this.buildClusterer(OnlineAppMap.getGoogleMarkers(experienceMarkersWithOutBenefits), calc);

        // Add the experience markers with benefits directly to the google map (AdvancedMarkerElement
        // attaches/detaches via the `.map` property, not setMap()).
        OnlineAppMap.getGoogleMarkers(experienceMarkersWithBenefits).forEach((googleMarker) => {
            googleMarker.map = this.googleMap;
        });

        if (this.experienceMarkers.length > 0) {
            this.zoomOutUntilMarkersVisible()
        } else {
            // Zoom to default?
        }
    }

    clearMarkersFromMap() {
        this.destroyClusterer();
        this.experienceMarkers.forEach( (experienceMarker) => {
            experienceMarker.googleMarker.map = null;
        });
    }

    // Tear down the marker clusterer safely. We must NOT call the plain clearMarkers() (which triggers
    // a render() over an empty marker set): SuperClusterViewportAlgorithm's render reads the map
    // projection and re-queries supercluster.getClusters(), both of which throw when the clusterer is
    // empty or not yet drawn (null projection / "reading 'range'"). Instead: clearMarkers(true) empties
    // the markers array WITHOUT rendering (and without detaching markers we may re-add to the next
    // clusterer), then setMap(null) removes the drawn cluster bubbles via the clusterer's onRemove/reset.
    destroyClusterer() {
        if (!this.markerClusterer) return;
        this.markerClusterer.clearMarkers(true);
        this.markerClusterer.setMap(null);
        this.markerClusterer = null;
    }

    setCurrentZoom() {
        this.removeHtmlClassState(`zoom_${ getZoomLabel(this.currentZoom) }`);
        this.currentZoom = this.googleMap.getZoom();
        this.addHtmlClassState(`zoom_${ getZoomLabel(this.currentZoom) }`);
    }

    zoomOutUntilMarkersVisible() {
        if (this.experienceMarkers.length < 1 || !this.googleMap.getBounds()) { return };
        while (!this.experienceMarkers.some(function (experienceMarker, index, array) {
            return this.googleMap.getBounds().contains(experienceMarker.googleMarker.position)
        }.bind(this))) {
            this.googleMap.setZoom(this.googleMap.getZoom() - 1)
        }
    }

    zoomMapToFitExperienceMarkers(experienceMarkers) {
        const bounds = new this.googleMapsApi.LatLngBounds();
        const googleLatLngsPositions = experienceMarkers.map(function(experienceMarker){
            return experienceMarker.googleMarker.position;
        });
        googleLatLngsPositions.forEach( (position) => { bounds.extend(position) } );
        this.googleMap.fitBounds(bounds);
    }

    zoomMapToFitBounds(mapBounds) {
        const bounds = new this.googleMapsApi.LatLngBounds(mapBounds.sw, mapBounds.ne);
        if (!bounds) return;
        this.googleMap.fitBounds(bounds);
    }

    // Render all the ExperienceMarkers for the MapFilter
    renderMapFilter(mapFilter){
        if (!this.onlineApp.isExperienceMarkersLoaded()) { return }

        requestAnimationFrame( () => {
            const filterExperienceMarkers = this.onlineApp.experienceMarkerCollection.allWithExperienceIds(mapFilter.experienceIds);
            this.replaceAllExperienceMarkers(this.withFocusedAdHocMarker(mapFilter, filterExperienceMarkers));
        });
    }

    // Reveal-resolver step 4: when a specific experience is focused but is NOT a member of the
    // active filter (it lives only in an unpublished filter — the ~58 cohort), append its marker
    // so the place still has a pin under its focus card. Rebuilt on every render, so it clears
    // automatically when the filter changes or the focus is cleared.
    // See plans/2026-07-27--experience-navigation-beyond-everything-filter.md.
    withFocusedAdHocMarker(mapFilter, experienceMarkers) {
        const focused = this.readState('experienceMarker');
        if (!focused) { return experienceMarkers }
        if (mapFilter.experienceIds.includes(focused.experienceId)) { return experienceMarkers }
        if (experienceMarkers.includes(focused)) { return experienceMarkers }
        return experienceMarkers.concat([focused]);
    }

    eraseMapFilter(mapFilter) {
        this.replaceAllExperienceMarkers([]);
    }

    isRenderedWith(mapFilter) {
        return this.currentMapFilter === mapFilter;
    }

    renderGeoData(geoData) {
        if (!!geoData) {
            // Don't add geo data unless the id has changed…
            const newId = geoData['id']; // Our geo data should have an `id` field.
            if (this.currentGeoDataId !== newId) {
                removeGeoData(this.googleMap); // First remove any old data
                addGeoData(this.googleMap, geoData)
            }
            this.currentGeoDataId = newId;
        } else {
            removeGeoData(this.googleMap);
            this.currentGeoDataId = null;
        }
    }

    render() {
        const mapBounds = this.readState('mapBounds');
        if (mapBounds) this.zoomMapToFitBounds(mapBounds);

        this.renderGeoData(this.readState('geoData'));

        const mapFilter = this.readState('mapFilter');
        if ( mapFilter ) {
            this.removeHtmlClassState('no_map_filter');
            // Re-render not only when the filter changes, but also when the focused out-of-filter
            // (ad-hoc) experience changes — otherwise the ad-hoc pin wouldn't appear/clear while
            // the backdrop filter stays the same. See the reveal resolver step 4.
            const focused = this.readState('experienceMarker');
            const adHocExperienceId = (focused && !mapFilter.experienceIds.includes(focused.experienceId)) ? focused.experienceId : null;
            if (!this.isRenderedWith(mapFilter) || this.currentAdHocExperienceId !== adHocExperienceId) { this.renderMapFilter(mapFilter) };
            this.currentAdHocExperienceId = adHocExperienceId;
        } else {
            this.addHtmlClassState('no_map_filter');
            this.eraseMapFilter(mapFilter);
            this.currentAdHocExperienceId = null;
        }
        this.currentMapFilter = mapFilter;
    }

}

// Cluster icon pixel size by marker count magnitude (ports the old 5-size markerclustererplus `styles`).
function clusterIconSizeForCount(count) {
    if (count < 10) return 53;
    if (count < 100) return 56;
    if (count < 1000) return 66;
    if (count < 10000) return 78;
    return 90;
}

function addGeoData(map, geoData) {
    map.data.addGeoJson(geoData);

    // Set overall styles for the geo data
    const styleOptions = (geoData['styleOptions'] || DefaultGeoDataStyleOptions);
    map.data.setStyle(styleOptions);

    // Override specific feature styles
    map.data.forEach((feature) => {
        const styleOptions = feature.getProperty('styleOptions') || null;
        map.data.overrideStyle(feature, styleOptions);
    });
}

function removeGeoData(map) {
    map.data.forEach((feature) => {
        map.data.remove(feature);
    });
    map.data.setStyle({});
}

// https://developers.google.com/maps/documentation/javascript/reference/data#Data.StyleOptions
const DefaultGeoDataStyleOptions = {
    strokeColor: '#000',
    strokeWeight: 4,
    strokeOpacity: 1
};